import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { 
  ArrowLeft, Search, Plus, User, Users, Mail, Phone, MapPin, 
  Edit, Trash2, MessageSquare, Send, Heart, Calendar, Download, Printer,
  Eye, X, Check, Gift
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const REFERRAL_SOURCE_TYPES = [
  "Dentist",
  "Patient",
  "Friend/Family",
  "Insurance",
  "Online/Website",
  "Other"
];

const InboundReferralsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { practice } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('dentalToken');

  // State
  const [sources, setSources] = useState([]);
  const [filteredSources, setFilteredSources] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('all');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  
  // Selected source
  const [selectedSource, setSelectedSource] = useState(null);
  const [sourceDetails, setSourceDetails] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    sourceType: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [sendingThankYou, setSendingThankYou] = useState(false);

  useEffect(() => {
    loadSources();
  }, []);

  // Filter sources based on search and type filter
  useEffect(() => {
    let filtered = sources;
    
    // Apply type filter
    if (selectedTypeFilter !== 'all') {
      filtered = filtered.filter(s => s.sourceType === selectedTypeFilter);
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        s.name?.toLowerCase().includes(query) ||
        s.email?.toLowerCase().includes(query) ||
        s.phone?.includes(query)
      );
    }
    
    setFilteredSources(filtered);
  }, [sources, searchQuery, selectedTypeFilter]);

  const loadSources = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/inbound-referrals/sources`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSources(data.sources || []);
      setFilteredSources(data.sources || []);
    } catch (error) {
      console.error('Load sources error:', error);
      toast({ title: "Error", description: "Failed to load referral sources", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const loadSourceDetails = async (sourceId) => {
    try {
      const response = await fetch(`${backendUrl}/api/inbound-referrals/sources/${sourceId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSourceDetails(data);
    } catch (error) {
      console.error('Load source details error:', error);
      toast({ title: "Error", description: "Failed to load referral source details", variant: "destructive" });
    }
  };

  const handleAddSource = async () => {
    if (!formData.name.trim()) {
      toast({ title: "Error", description: "Name is required", variant: "destructive" });
      return;
    }
    if (!formData.sourceType) {
      toast({ title: "Error", description: "Please select a referral type", variant: "destructive" });
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${backendUrl}/api/inbound-referrals/sources`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add referral source');
      }

      toast({ title: "Success", description: "Referral source added successfully" });
      setShowAddModal(false);
      resetForm();
      await loadSources();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSource = async () => {
    if (!formData.name.trim()) {
      toast({ title: "Error", description: "Name is required", variant: "destructive" });
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${backendUrl}/api/inbound-referrals/sources/${selectedSource.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update referral source');
      }

      toast({ title: "Success", description: "Referral source updated successfully" });
      setShowEditModal(false);
      resetForm();
      await loadSources();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSource = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/inbound-referrals/sources/${selectedSource.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete referral source');
      }

      toast({ title: "Success", description: "Referral source deleted successfully" });
      setShowDeleteConfirm(false);
      setShowViewModal(false);
      setSelectedSource(null);
      await loadSources();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleSendThankYou = async (patientId) => {
    if (!selectedSource || !patientId) return;

    try {
      setSendingThankYou(true);
      const response = await fetch(`${backendUrl}/api/inbound-referrals/send-thank-you/${selectedSource.id}?patient_id=${patientId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send thank you');
      }

      const results = data.results;
      let message = "Thank you sent!";
      const tierLabel = results.email?.tier || '';
      if (results.email.sent && results.sms.sent) {
        message = `Thank you sent via email and text!${tierLabel ? ` (${tierLabel})` : ''}`;
      } else if (results.email.sent) {
        message = `Thank you sent via email!${tierLabel ? ` (${tierLabel})` : ''}`;
      } else if (results.sms.sent) {
        message = "Thank you sent via text!";
      }

      toast({ title: "Success", description: message });
      setShowThankYouModal(false);
      // Refresh source details to show updated thank you history
      await loadSourceDetails(selectedSource.id);
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSendingThankYou(false);
    }
  };

  const openViewModal = async (source) => {
    setSelectedSource(source);
    await loadSourceDetails(source.id);
    setShowViewModal(true);
  };

  const openEditModal = (source) => {
    setSelectedSource(source);
    setFormData({
      name: source.name || '',
      sourceType: source.sourceType || '',
      email: source.email || '',
      phone: source.phone || '',
      address: source.address || '',
      notes: source.notes || ''
    });
    setShowEditModal(true);
  };

  const openDeleteConfirm = (source) => {
    setSelectedSource(source);
    setShowDeleteConfirm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      sourceType: '',
      email: '',
      phone: '',
      address: '',
      notes: ''
    });
    setSelectedSource(null);
  };

  const getTypeColor = (type) => {
    const colors = {
      'Dentist': 'bg-blue-100 text-blue-800',
      'Patient': 'bg-green-100 text-green-800',
      'Friend/Family': 'bg-pink-100 text-pink-800',
      'Insurance': 'bg-purple-100 text-purple-800',
      'Online/Website': 'bg-orange-100 text-orange-800',
      'Other': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Dentist': return <User className="h-4 w-4" />;
      case 'Patient': return <Users className="h-4 w-4" />;
      case 'Friend/Family': return <Heart className="h-4 w-4" />;
      default: return <User className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading referral sources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/practice/dashboard')}
                className="flex items-center"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Inbound Referrals</h1>
                <p className="text-gray-600">Manage people who refer patients to your practice</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">{practice?.name}</p>
              <p className="text-xs text-gray-500">Practice Administrator</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          
          {/* Type Filter */}
          <Select value={selectedTypeFilter} onValueChange={setSelectedTypeFilter}>
            <SelectTrigger className="w-48" data-testid="type-filter">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {REFERRAL_SOURCE_TYPES.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Add Button */}
          <Button onClick={() => setShowAddModal(true)} data-testid="add-source-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Referral Source
          </Button>
        </div>
      </div>

      {/* Sources Grid */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSources.map((source) => (
            <Card key={source.id} className="hover:shadow-lg transition-shadow" data-testid={`source-card-${source.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg flex items-center">
                    {getTypeIcon(source.sourceType)}
                    <span className="ml-2">{source.name}</span>
                  </CardTitle>
                  <Badge className={getTypeColor(source.sourceType)}>
                    {source.sourceType}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  {source.email && (
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="truncate">{source.email}</span>
                    </div>
                  )}
                  {source.phone && (
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{source.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center text-green-600">
                    <Gift className="h-4 w-4 mr-2" />
                    <span>{source.totalReferrals || 0} referral{source.totalReferrals !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => openViewModal(source)}
                    className="flex-1"
                    data-testid={`view-source-${source.id}`}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditModal(source)}
                    className="flex-1"
                    data-testid={`edit-source-${source.id}`}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filteredSources.length === 0 && !loading && (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No referral sources found</h3>
            <p className="text-gray-600">
              {searchQuery || selectedTypeFilter !== 'all' 
                ? 'Try adjusting your filters' 
                : 'Add your first referral source to start tracking referrals'}
            </p>
            {!searchQuery && selectedTypeFilter === 'all' && (
              <Button onClick={() => setShowAddModal(true)} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Referral Source
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Add Source Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Referral Source</DialogTitle>
            <DialogDescription>
              Add a person or entity who refers patients to your practice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Dr. John Smith, Jane Doe"
                data-testid="source-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Referral Type *</label>
              <Select value={formData.sourceType} onValueChange={(v) => setFormData({...formData, sourceType: v})}>
                <SelectTrigger data-testid="source-type-select">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {REFERRAL_SOURCE_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="email@example.com"
                data-testid="source-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="(555) 123-4567"
                data-testid="source-phone-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Address</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="123 Main St, City, State"
                data-testid="source-address-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional notes..."
                className="w-full p-2 border rounded-md text-sm"
                rows={2}
                data-testid="source-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleAddSource} disabled={saving} data-testid="save-source-btn">
              {saving ? 'Saving...' : 'Add Source'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Source Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Referral Source</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Referral Type *</label>
              <Select value={formData.sourceType} onValueChange={(v) => setFormData({...formData, sourceType: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {REFERRAL_SOURCE_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Address</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full p-2 border rounded-md text-sm"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleUpdateSource} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Source Details Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center">
                {selectedSource && getTypeIcon(selectedSource.sourceType)}
                <span className="ml-2">{selectedSource?.name}</span>
              </DialogTitle>
              {selectedSource && (
                <Badge className={getTypeColor(selectedSource.sourceType)}>
                  {selectedSource.sourceType}
                </Badge>
              )}
            </div>
          </DialogHeader>
          
          {sourceDetails && (
            <div className="space-y-6 py-4">
              {/* Contact Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold mb-3">Contact Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{sourceDetails.source.email || 'No email'}</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{sourceDetails.source.phone || 'No phone'}</span>
                  </div>
                  {sourceDetails.source.address && (
                    <div className="flex items-center col-span-2">
                      <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{sourceDetails.source.address}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-green-800 text-sm">Total Referrals</h3>
                      <p className="text-3xl font-bold text-green-600">{sourceDetails.totalReferrals}</p>
                    </div>
                    <Gift className="h-10 w-10 text-green-400" />
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-purple-800 text-sm">Thank Yous Sent</h3>
                      <p className="text-3xl font-bold text-purple-600">{sourceDetails.totalThankYousSent || 0}</p>
                    </div>
                    <Heart className="h-10 w-10 text-purple-400" />
                  </div>
                  {sourceDetails.currentTier && (
                    <Badge data-testid="source-tier-badge" className="mt-2 bg-purple-100 text-purple-700 border-purple-300 text-xs">
                      {sourceDetails.currentTier.label}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Referred Patients */}
              <div>
                <h3 className="font-semibold mb-3">Patients Referred</h3>
                {sourceDetails.referredPatients.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {sourceDetails.referredPatients.map((patient, idx) => (
                      <div key={patient.id || idx} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg" data-testid={`referred-patient-${patient.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{patient.firstName} {patient.lastName}</p>
                          <p className="text-xs text-gray-500">
                            {patient.createdAt ? new Date(patient.createdAt).toLocaleDateString() : 'N/A'}
                          </p>
                          {patient.thankYouSentOn && (
                            <div className="flex items-center gap-1 mt-1" data-testid={`thank-you-status-${patient.id}`}>
                              <Check className="h-3 w-3 text-green-600" />
                              <span className="text-xs text-green-600 font-medium">
                                Thank You Sent {new Date(patient.thankYouSentOn).toLocaleDateString()}
                              </span>
                              {patient.thankYouTier && (
                                <Badge variant="outline" className="text-xs py-0 px-1 ml-1 text-purple-600 border-purple-300">
                                  {patient.thankYouTier}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            handleSendThankYou(patient.id);
                          }}
                          disabled={sendingThankYou}
                          data-testid={`send-thank-you-btn-${patient.id}`}
                        >
                          <Heart className="h-4 w-4 mr-1 text-pink-500" />
                          {patient.thankYouSentOn ? 'Resend' : 'Thank You'}
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No patients referred yet</p>
                )}
              </div>

              {/* Notes */}
              {sourceDetails.source.notes && (
                <div>
                  <h3 className="font-semibold mb-2">Notes</h3>
                  <p className="text-gray-600 text-sm bg-gray-50 p-3 rounded-lg">
                    {sourceDetails.source.notes}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => openDeleteConfirm(selectedSource)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowViewModal(false);
                openEditModal(selectedSource);
              }}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button onClick={() => setShowViewModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Referral Source</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedSource?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteSource}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InboundReferralsPage;
